import UserAgreementForm from "@via/pages/authentication/self-service-signup/progress/user-agreement-form.tsx";
import PaymentMethodForm from "@via/pages/authentication/self-service-signup/progress/payment-method-form.tsx";
import ConfirmationScreen from "@via/pages/authentication/self-service-signup/progress/confirmation-screen.tsx";
import { ElementType, FC, useEffect, useMemo, useState } from "react";
import FoundryConnectLogo from "@via/components/foundry-connect-logo.tsx";
import { StepProgress } from "@via/components/step-progress.tsx";
import { OverlayLoading } from "@via/components/loading/loading.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { OnboardingStep } from "common";
import {
  OnboardingStepKey,
  resolveStepIndex,
  useAgreementStep,
} from "@via/pages/authentication/self-service-signup/progress/onboarding-steps.ts";

const UPGRADE_STEPS: Partial<
  Record<OnboardingStepKey, { name: string; component: ElementType }>
> = {
  agreement: { name: "User Agreement", component: UserAgreementForm },
  payment: { name: "Payment Method", component: PaymentMethodForm },
  confirmation: { name: "Confirmation", component: ConfirmationScreen },
};

interface UserUpgradeProgressProps {
  onClose: () => void;
}

const UserUpgradeProgress: FC<UserUpgradeProgressProps> = ({ onClose }) => {
  const { user } = useAuth();
  const email = user.email;
  const [currentStepKey, setCurrentStepKey] =
    useState<OnboardingStepKey>("agreement");

  const { skipAgreement, isAgreementLoading } = useAgreementStep(
    Boolean(user?.id)
  );

  const stepKeys = useMemo<OnboardingStepKey[]>(
    () => [
      ...(skipAgreement ? [] : (["agreement"] as const)),
      "payment",
      "confirmation",
    ],
    [skipAgreement]
  );

  const currentStepIndex = resolveStepIndex(stepKeys, currentStepKey);

  const steps = useMemo(() => {
    return stepKeys.map((key, index) => ({
      name: UPGRADE_STEPS[key]?.name ?? "",
      isCompleted: index < currentStepIndex,
    }));
  }, [currentStepIndex, stepKeys]);

  const handleNextStep = () => {
    if (currentStepIndex < stepKeys.length - 1) {
      setCurrentStepKey(stepKeys[currentStepIndex + 1]);
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex - 1 > -1) {
      setCurrentStepKey(stepKeys[currentStepIndex - 1]);
    }
  };

  // Steps of the upgrade flow are never revisited from the step bar.
  const handleStepClick = () => {};

  // Set initial step based on user's onboarding status. A step that is not listed resolves to
  // the one after it, see resolveStepIndex.
  useEffect(() => {
    if (!user?.onboardingStep) return;
    if (user.onboardingStep === OnboardingStep.AccountCreated) {
      setCurrentStepKey("agreement");
    } else if (user.onboardingStep === OnboardingStep.AgreementsAccepted) {
      setCurrentStepKey("payment");
    } else if (user.onboardingStep === OnboardingStep.PaymentCompleted) {
      setCurrentStepKey("confirmation");
    }
  }, [user?.onboardingStep, user?.userOrganizations?.[0]?.status]);

  const CurrentStepComponent =
    UPGRADE_STEPS[stepKeys[currentStepIndex]]?.component;

  const handleUserUpgradeEnd = () => {
    onClose();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8">
      <FoundryConnectLogo />
      {isAgreementLoading ? (
        <OverlayLoading />
      ) : (
        <div className="flex flex-col items-center justify-center w-full max-w-3xl">
          <StepProgress
            steps={steps}
            currentStepIndex={currentStepIndex}
            onStepClick={handleStepClick}
          />
          <div className="mt-10">
            {CurrentStepComponent && (
              <CurrentStepComponent
                isUpgrade={true}
                onUserUpgradeEnd={handleUserUpgradeEnd}
                onNextStep={handleNextStep}
                onPreviousStep={handlePreviousStep}
                email={email}
                mode="card"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserUpgradeProgress;
